import React, { useState, useEffect } from 'react';
import { X, UserPlus, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

// 13-language labels — kept local so we don't bloat the global translations file.
const L = {
  fr: { followers: 'Abonnés', following: 'Abonnements', empty_f: 'Aucun abonné', empty_g: 'Aucun abonnement', loading: 'Chargement…', subscribed: 'Abonné', unsubscribed: 'Désabonné', error: 'Erreur' },
  en: { followers: 'Followers', following: 'Following', empty_f: 'No followers', empty_g: 'Not following anyone', loading: 'Loading…', subscribed: 'Following', unsubscribed: 'Unfollowed', error: 'Error' },
  es: { followers: 'Seguidores', following: 'Siguiendo', empty_f: 'Sin seguidores', empty_g: 'Aún no sigues a nadie', loading: 'Cargando…', subscribed: 'Siguiendo', unsubscribed: 'Dejado de seguir', error: 'Error' },
  pt: { followers: 'Seguidores', following: 'Seguindo', empty_f: 'Sem seguidores', empty_g: 'Ainda não segue ninguém', loading: 'A carregar…', subscribed: 'A seguir', unsubscribed: 'Deixou de seguir', error: 'Erro' },
  de: { followers: 'Follower', following: 'Folge ich', empty_f: 'Keine Follower', empty_g: 'Noch niemandem folgen', loading: 'Lädt…', subscribed: 'Folge ich', unsubscribed: 'Entfolgt', error: 'Fehler' },
  it: { followers: 'Follower', following: 'Seguiti', empty_f: 'Nessun follower', empty_g: 'Non segui nessuno', loading: 'Caricamento…', subscribed: 'Segui', unsubscribed: 'Smesso di seguire', error: 'Errore' },
  ru: { followers: 'Подписчики', following: 'Подписки', empty_f: 'Нет подписчиков', empty_g: 'Пока никого не подписаны', loading: 'Загрузка…', subscribed: 'Подписаны', unsubscribed: 'Отписаны', error: 'Ошибка' },
  ar: { followers: 'المتابعون', following: 'يتابع', empty_f: 'لا يوجد متابعون', empty_g: 'لا تتابع أحدًا بعد', loading: 'جارٍ التحميل…', subscribed: 'تمّت المتابعة', unsubscribed: 'أُلغيت المتابعة', error: 'خطأ' },
  zh: { followers: '粉丝', following: '关注', empty_f: '暂无粉丝', empty_g: '暂未关注任何人', loading: '加载中…', subscribed: '已关注', unsubscribed: '已取消关注', error: '错误' },
  ja: { followers: 'フォロワー', following: 'フォロー中', empty_f: 'フォロワーなし', empty_g: 'まだ誰もフォローしていません', loading: '読み込み中…', subscribed: 'フォローしました', unsubscribed: 'フォロー解除しました', error: 'エラー' },
  hi: { followers: 'फ़ॉलोअर', following: 'फ़ॉलोइंग', empty_f: 'कोई फ़ॉलोअर नहीं', empty_g: 'अभी किसी को फ़ॉलो नहीं करते', loading: 'लोड हो रहा है…', subscribed: 'फ़ॉलो कर रहे हैं', unsubscribed: 'अनफ़ॉलो किया', error: 'त्रुटि' },
  nl: { followers: 'Volgers', following: 'Volgend', empty_f: 'Geen volgers', empty_g: 'Nog niemand gevolgd', loading: 'Laden…', subscribed: 'Gevolgd', unsubscribed: 'Ontvolgd', error: 'Fout' },
  sw: { followers: 'Wafuasi', following: 'Anayefuatwa', empty_f: 'Hakuna wafuasi', empty_g: 'Bado hufuati mtu yeyote', loading: 'Inapakia…', subscribed: 'Umefuata', unsubscribed: 'Umeacha kufuata', error: 'Hitilafu' },
  ht: { followers: 'Abòne', following: 'W ap swiv', empty_f: 'Pa gen abòne', empty_g: 'Ou pa swiv pèsòn', loading: 'K ap chaje…', subscribed: 'W ap swiv', unsubscribed: 'Sispann swiv', error: 'Erè' },
};

// Fallback avatar SVG (letter placeholder inside a magenta circle)
const initialsDataUri = (name) => {
  const ch = ((name || '?')[0] || '?').toUpperCase();
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'><defs><linearGradient id='g' x1='0' x2='1' y1='0' y2='1'><stop offset='0' stop-color='%23d946ef'/><stop offset='1' stop-color='%237c3aed'/></linearGradient></defs><rect width='96' height='96' rx='48' fill='url(%23g)'/><text x='50%' y='54%' font-family='Arial,Helvetica,sans-serif' font-size='38' font-weight='700' fill='white' text-anchor='middle' dominant-baseline='middle'>${ch}</text></svg>`;
  return `data:image/svg+xml;utf8,${svg}`;
};

const FollowersModal = ({ userId, type, onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingMap, setFollowingMap] = useState({});
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { language } = useLanguage();
  const l = L[language] || L.fr;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const endpoint = type === 'followers'
          ? `/api/users/${userId}/followers`
          : `/api/users/${userId}/following`;
        const response = await axios.get(endpoint);
        if (!cancelled) setUsers(response.data || []);

        if (currentUser) {
          const me = await axios.get(`/api/users/${currentUser.id}`);
          if (cancelled) return;
          const map = {};
          (me.data.following || []).forEach((id) => { map[id] = true; });
          setFollowingMap(map);
        }
      } catch (e) {
        if (!cancelled) toast.error(l.error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, type, currentUser?.id]);

  const handleFollow = async (targetUserId) => {
    try {
      const response = await axios.post(`/api/users/${targetUserId}/follow`);
      setFollowingMap((prev) => ({ ...prev, [targetUserId]: response.data.following }));
      toast.success(response.data.following ? l.subscribed : l.unsubscribed);
    } catch (e) {
      toast.error(l.error);
    }
  };

  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      data-testid="followers-modal"
    >
      <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold" data-testid="followers-modal-title">
            {type === 'followers' ? l.followers : l.following}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-400">{l.loading}</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {type === 'followers' ? l.empty_f : l.empty_g}
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                  <div className="flex items-center space-x-3 flex-1">
                    <button
                      onClick={() => { navigate(`/profile/${user.id}`); onClose(); }}
                      className="w-12 h-12 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center overflow-hidden hover:opacity-80 transition-opacity"
                      data-testid={`followers-user-${user.id}`}
                    >
                      <img
                        src={user.profile_image_url ? getMediaUrl(user.profile_image_url) : initialsDataUri(user.name)}
                        alt={user.name || ''}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.src = initialsDataUri(user.name); }}
                      />
                    </button>
                    <div className="flex-1 min-w-0">
                      <button onClick={() => { navigate(`/profile/${user.id}`); onClose(); }} className="block text-left">
                        <h3 className="font-semibold hover:text-magenta-400 transition-colors truncate">{user.name}</h3>
                        <p className="text-sm text-gray-400 truncate">@{user.username}</p>
                      </button>
                    </div>
                  </div>
                  {currentUser && currentUser.id !== user.id && (
                    <button
                      onClick={() => handleFollow(user.id)}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-full font-semibold transition-all text-sm ${
                        followingMap[user.id]
                          ? 'bg-white/10 text-white hover:bg-white/20'
                          : 'bg-gradient-to-r from-magenta-500 to-violet-600 text-white hover:from-magenta-600 hover:to-violet-700'
                      }`}
                    >
                      {followingMap[user.id] ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowersModal;
